import { Box, Typography, Grid, Button, Paper, Stack, useTheme } from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ExploreIcon from '@mui/icons-material/Explore';
import AddIcon from '@mui/icons-material/Add';
import { Link, useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import { ANALYTICS_PLATFORM, trackProductEvent } from '@/lib/analytics/product-events';

const InvestorInvestmentsEmptyState = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { trackEvent } = useAnalytics();

  const handleExploreOpportunities = () => {
    navigate('/oportunidades');
  };

  const handleAddInvestmentClick = () => {
    trackProductEvent(trackEvent, 'add_investment_clicked', {
      entry_point: 'empty_state',
      role: 'investor',
      platform: ANALYTICS_PLATFORM,
    });
  };

  return (
    <Grid item xs={12}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4, md: 6 },
          textAlign: 'center',
          backgroundColor: 'grey.50',
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <BusinessCenterIcon
            sx={{
              fontSize: { xs: '3rem', sm: '4rem' },
              color: 'grey.400',
              mb: 2,
            }}
          />
          <Typography
            variant="h4"
            gutterBottom
            fontWeight={600}
            color="text.primary"
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
          >
            Comienza tu viaje como inversor
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 4,
              maxWidth: '600px',
              mx: 'auto',
              fontSize: { xs: '0.95rem', sm: '1rem' },
              lineHeight: 1.6,
            }}
          >
            Aún no tienes inversiones registradas. Puedes crear inversiones de dos formas:
            registrando manualmente una propiedad que ya tienes, o explorando oportunidades
            publicadas por PSIs (Personal Shopper Inmobiliarios) profesionales.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            component={Link}
            to="/inversiones/nueva/"
            state={{ entryPoint: 'empty_state' }}
            onClick={handleAddInvestmentClick}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              minWidth: { xs: '100%', sm: '200px' },
            }}
          >
            Registrar Inversión
          </Button>

          <Button
            variant="outlined"
            size="large"
            startIcon={<ExploreIcon />}
            onClick={handleExploreOpportunities}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              minWidth: { xs: '100%', sm: '200px' },
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.50',
              },
            }}
          >
            Explorar Oportunidades
          </Button>
        </Stack>

        <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.grey[200]}` }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={4}
            justifyContent="center"
            divider={
              <Box
                sx={{
                  width: { xs: '100%', sm: '1px' },
                  height: { xs: '1px', sm: '40px' },
                  backgroundColor: 'grey.300',
                }}
              />
            }
          >
            <Box sx={{ textAlign: 'center', maxWidth: '250px' }}>
              <HandshakeIcon sx={{ fontSize: '2rem', color: 'primary.main', mb: 1 }} />
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Trabajar con PSIs
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Colabora con expertos que buscan y analizan propiedades por ti
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', maxWidth: '250px' }}>
              <AddIcon sx={{ fontSize: '2rem', color: 'primary.main', mb: 1 }} />
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Registro Manual
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Añade propiedades que ya posees para gestionar tu portfolio
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Grid>
  );
};

export default InvestorInvestmentsEmptyState;
